import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionButton, Banner, BannerItemsType, Button, Modal } from '../../../stories/molecules';
import { ContainerV2, RowFlexContainer } from '../../../stories/atoms/containerV2/ContainerV2';
import { Typography } from '../../../stories/atoms';
import { StatusTableitem } from '../../accessApproval/statusTableItem/StatusTableItem';
import { convertCamelCaseToTitleCase, isFailureResponse } from '../../../utils';
import { AccessRequestItem, UserAccessRequest, useUserPermissions } from '../../../hooks/useUserPermissions';
import { accessesRequestedTranslations, commonTranslations, profileTranslations } from '../../../translations';
import { accessRequestTranslations } from '../../../translations/accessRequest';
import { getDaysLeftForAccessExpiry } from '../../accessApproval/util';
import { useUser } from '../../../hooks';
import { useAsyncAction } from '../../../hooks/useAsyncAction';

const getSuccessBanner = (label: string): BannerItemsType[] => [
  {
    id: 'reapply-success',
    type: 'success',
    label,
    autoDismiss: false,
  },
];

const getErrorBanner = (label: string, onTryAgain: () => void): BannerItemsType[] => [
  {
    id: 'reapply-error',
    type: 'error',
    label,
    actionButton: ActionButton.TryAgain,
    onTryAgain,
  },
];

export type ViewRequestModalProps = {
  request: AccessRequestItem;
  onClose: () => void;
  onReapplySuccess?: () => void;
};

export const ViewRequestModal: FC<ViewRequestModalProps> = ({ request, onClose, onReapplySuccess }) => {
  const { t } = useTranslation();
  const { userAccessRequest } = useUserPermissions();
  const {
    get: { profile },
  } = useUser();

  const [bannerItems, setBannerItems] = useState<BannerItemsType[]>([]);

  const { fullName, role } = profileTranslations;
  const {
    status,
    manageAccessModal: { requestID },
  } = accessRequestTranslations;
  const { viewRequest, viewRequestModal, reapply, reapplySuccess, reapplyError } = accessesRequestedTranslations;

  const { id, requestedAssignment } = request;
  const { title, organizationId, organizationName } = requestedAssignment;
  const requestFields = [
    { label: requestID, value: id },
    { label: role, value: title, transform: convertCamelCaseToTitleCase },
    { label: viewRequestModal.organizationId, value: organizationId },
    { label: viewRequestModal.organizationName, value: organizationName },
  ];

  const isRejectedOrExpired = request.status === 'Rejected' || request.status === 'Expired';

  const shouldShowReapplyButton = () => {
    const isApprovedWithExpiry =
      request.status === 'Approved' && getDaysLeftForAccessExpiry(request.statusTimestamp, 365) <= 5;
    return isRejectedOrExpired || isApprovedWithExpiry;
  };

  const {
    isLoading: isReapplying,
    isError: isReapplyError,
    result: reapplyResult,
    triggerAction: triggerReapply,
    reset: resetReapply,
  } = useAsyncAction<UserAccessRequest>();

  const reapplied = localStorage.getItem(`reapplied-${id}`) === 'true';

  const reapplyAccessRequest = async () => {
    const requestBody = {
      jobRoleCode: requestedAssignment.jobRoleCode ?? 'NOT SET',
      countryCode: requestedAssignment.countryCode ?? 'NOT SET',
      organizationId: requestedAssignment.organizationId ?? 'NOT SET',
      organizationName: requestedAssignment.organizationName ?? 'NOT SET',
      businessUnitId: requestedAssignment.businessUnitId ?? 'NOT SET',
      businessUnitName: requestedAssignment.businessUnitName ?? 'NOT SET',
      brandId: requestedAssignment.brandId ?? -1,
    };

    const response = await userAccessRequest(requestBody);

    if (isFailureResponse(response)) {
      throw new Error();
    }

    return response.data;
  };

  const handleReapply = () => {
    triggerReapply(reapplyAccessRequest);
  };

  useEffect(() => {
    if (reapplied) {
      setBannerItems(getSuccessBanner(t(reapplySuccess.key, reapplySuccess.defaultValue)));
    }
  }, [id]);

  useEffect(() => {
    if (reapplyResult && !isFailureResponse(reapplyResult)) {
      localStorage.setItem(`reapplied-${id}`, 'true');
      onReapplySuccess?.();
      setBannerItems(getSuccessBanner(t(reapplySuccess.key, reapplySuccess.defaultValue)));
    } else if (isReapplyError) {
      setBannerItems(
        getErrorBanner(t(reapplyError.key, reapplyError.defaultValue), () => {
          resetReapply();
          handleReapply();
        }),
      );
    }
  }, [reapplyResult, isReapplyError]);

  return (
    <Modal isOpen handleClose={onClose} transitionType="grow" ariaDescribedby="view-request-modal">
      <ContainerV2 padding={[4, 8]} data-testid="view-request-modal">
        <Typography type="h4" gutterBottom>
          {t(viewRequest.key, viewRequest.defaultValue)}
        </Typography>

        {(reapplied || isReapplyError) && <Banner isSticky items={bannerItems} onChange={() => null} />}

        <Typography type="body3">
          {convertCamelCaseToTitleCase(
            t(fullName.key, {
              defaultValue: fullName.defaultValue,
              firstName: profile.firstName,
              lastName: profile.lastName,
            }),
          )}
        </Typography>

        <Typography type="caption">{profile.upn}</Typography>

        <ContainerV2 margin={[4, 0]} hasBorder borderColor="hoverGray" padding={[2, 4]} borderRadius={[2]}>
          {requestFields.map(({ label, value, transform }) => (
            <RowFlexContainer key={label.key} justifyContent="between" gap={[4]} padding={[2, 0]}>
              <Typography>{t(label.key, label.defaultValue)}</Typography>
              <Typography>{value ? (transform ? transform(value) : value) : ''}</Typography>
            </RowFlexContainer>
          ))}

          <RowFlexContainer justifyContent="between" gap={[4]} padding={[2, 0]}>
            <Typography>{t(status.key, status.defaultValue)}</Typography>
            <StatusTableitem accessRequestItem={request} />
          </RowFlexContainer>
        </ContainerV2>

        <RowFlexContainer gap={[2]}>
          {shouldShowReapplyButton() && (
            <Button
              onClick={handleReapply}
              label={t(reapply.key, reapply.defaultValue)}
              fullWidth
              variant="secondary"
              disabled={reapplied || isReapplying}
              isLoading={isReapplying}
            />
          )}
          <Button
            onClick={onClose}
            label={t(commonTranslations.close.key, commonTranslations.close.defaultValue)}
            fullWidth
          />
        </RowFlexContainer>
      </ContainerV2>
    </Modal>
  );
};
